import { useEffect, useRef } from 'react';

const FOCUS_RETRY_DELAY_MS = 50;
const FOCUSABLE_ELEMENTS_SELECTOR =
  'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])';

/**
 * Hook to trap focus within a specific element.
 * - Moves focus to the first focusable element when mounted/activated.
 * - Traps focus within the container (Tab cycles through elements).
 * - Restores focus to the previously focused element on unmount/deactivation.
 *
 * @param ref - Ref to the container element to trap focus in.
 * @param isActive - Boolean to enable/disable the trap.
 */
export const useFocusTrap = (
  ref: React.RefObject<HTMLElement>,
  isActive: boolean,
  focusFirstElement = true,
) => {
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isActive) {
      // Save currently focused element
      previousFocus.current = document.activeElement as HTMLElement;

      const element = ref.current;
      if (!element) return;

      const getFocusableElements = () =>
        element.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR);

      // Focus the first focusable element, or the container itself
      const tryFocus = () => {
        const activeElement = document.activeElement;
        const isFocusInside = element.contains(activeElement);
        const isFocusOnContainer = activeElement === element;

        // Skip if focus is already on a focusable element inside (not the container itself)
        if (isFocusInside && !isFocusOnContainer) {
          return;
        }

        const focusableElements = getFocusableElements();
        const firstElement = focusableElements[0];

        if (firstElement) {
          firstElement.focus();
        } else if (element.getAttribute('tabindex') === '-1') {
          element.focus();
        }
      };

      // Focus the container first to ensure focus moves into the modal immediately
      if (element.getAttribute('tabindex') === '-1') {
        element.focus();
      }

      // Then attempt to focus the first focusable element
      // Use requestAnimationFrame to handle portal rendering timing
      const rafId = requestAnimationFrame(() => {
        if (focusFirstElement) tryFocus();
      });
      const timeoutId = focusFirstElement
        ? setTimeout(tryFocus, FOCUS_RETRY_DELAY_MS)
        : undefined;

      const handleTabKey = (e: KeyboardEvent) => {
        const focusableElements = getFocusableElements();
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      const keyListener = (e: KeyboardEvent) => handleTabKey(e);
      element.addEventListener('keydown', keyListener);

      return () => {
        cancelAnimationFrame(rafId);
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        element.removeEventListener('keydown', keyListener);
        // Restore focus
        if (previousFocus.current) {
          previousFocus.current.focus();
        }
      };
    }
  }, [focusFirstElement, isActive, ref]);
};
